import { IImpersonationContext, ITagCategory } from '@ViewModels';
import { FC, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useCreateTagCategoryMutation } from '../../../../../queries/TagCategory/useCreateTagCategoryMutation';
import { invalidateGetTagCategoriesQuery } from '../../../../../queries/TagCategory/useGetTagCategoriesQuery';
import { Column, Row } from '../../../../components-v2/PageLayout/Layout';
import { ThemeColors } from '../../../../components-v2/Theme/theme';
import { Button, ButtonBase } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { Input } from '../../../../components-v2/Input';
import { ColorPicker } from '../ColorPicker/ColorPicker';
import { styleSheet } from './styles';

interface CreateCategoryModalProps {
	isOpen: boolean;
	onClose: () => void;
	impersonationContext: IImpersonationContext;
	onCategoryCreated?: (category: ITagCategory | null) => void;
}

export const CreateCategoryModal: FC<CreateCategoryModalProps> = ({
	isOpen,
	onClose,
	impersonationContext,
	onCategoryCreated,
}) => {
	const errorMessages = useErrorMessages();
	const inputRef = useRef<HTMLInputElement>(null);
	const [color, setColor] = useState<keyof ThemeColors['pill']>();

	const handleClose = () => {
		setColor(null);
		onClose();
	};

	const createTagCategory = useCreateTagCategoryMutation({
		onSuccess: createdCategory => {
			const categoryName = inputRef.current.value;
			toast.success(`${categoryName} created`);
			invalidateGetTagCategoriesQuery();
			// Pass the created category to the callback
			onCategoryCreated?.(createdCategory);
			handleClose();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error(`${inputRef?.current?.value} could not be created`);
			handleClose();
		},
	});

	const handleCreateCategory = () => {
		createTagCategory.mutate({ categoryName: inputRef?.current?.value, tags: [], color, impersonationContext });
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose}>
			<Row styles={[styleSheet.headerRow]}>
				<ButtonBase onClick={handleClose}>
					<XIcon height={14} width={14} />
				</ButtonBase>
			</Row>
			<Column styles={[styleSheet.formContainer]}>
				<Row styles={[styleSheet.titleRow]}>
					<span>Add Tag Category</span>
				</Row>
				<Row styles={[styleSheet.formRow]}>
					<Input
						autoComplete='off'
						type='text'
						styles={[styleSheet.textInput]}
						placeholder='Category name'
						ref={inputRef}
					/>
					<span style={{ paddingBottom: '10px' }}>
						<ColorPicker color={color} onColorChange={value => setColor(value)} />
					</span>
				</Row>
				<Row styles={[styleSheet.buttonRow]}>
					<Button kind='primary' label='Save' onClick={handleCreateCategory} />
					<Button kind='reverse' label='Cancel' onClick={handleClose} />
				</Row>
			</Column>
		</Modal>
	);
};
